/**
 * Engine-aware SQL generator for the "Open in query editor" secondary action.
 *
 * Two flavors:
 * - Table drill: `SELECT * FROM <schema.table>`.
 * - Row drill:   `SELECT * FROM <schema.table> WHERE <pk1> = <v1> [AND ...]`.
 *
 * Reuses `quoteIdentifier` and `formatWhereValue` from `whereClauseBuilder` so
 * the output stays consistent with the rest of the app's emitted SQL.
 */

import {
  quoteIdentifier,
  buildWhereCondition,
} from './whereClauseBuilder';
import type { DatabaseEngineId } from '../../../../core/engines';

export type DrillTarget =
  | {
      readonly kind: 'table';
      readonly schema: string;
      readonly table: string;
    }
  | {
      readonly kind: 'row';
      readonly schema: string;
      readonly table: string;
      readonly pk: ReadonlyArray<{ readonly column: string; readonly value: unknown }>;
    };

function qualifiedName(schema: string, table: string, engineId: DatabaseEngineId): string {
  const qTable = quoteIdentifier(table, engineId);
  if (!schema) return qTable;
  const qSchema = quoteIdentifier(schema, engineId);
  return `${qSchema}.${qTable}`;
}

export function buildDrillSql(target: DrillTarget, engineId: DatabaseEngineId): string {
  const fqn = qualifiedName(target.schema, target.table, engineId);
  if (target.kind === 'table') {
    return `SELECT * FROM ${fqn};`;
  }
  if (target.pk.length === 0) {
    return `SELECT * FROM ${fqn};`;
  }
  const conditions = target.pk
    .map((p) => buildWhereCondition(p.column, p.value, engineId))
    .join('\n  AND ');
  return `SELECT * FROM ${fqn}\nWHERE ${conditions};`;
}
